// @mui
import React from 'react';

import { styled } from '@mui/material/styles';
import type { Theme, SxProps } from '@mui/material/styles';

// ----------------------------------------------------------------------

type OwnerState = {
  color: string;
};

const RootStyle = styled('span', {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => {
  const { color } = ownerState;

  // Calculate readable text color based on background brightness
  const getReadableColor = (col: string): string => {
    if (!col || col.length !== 7) return '#000000';
    const r = parseInt(col.slice(1, 3), 16);
    const g = parseInt(col.slice(3, 5), 16);
    const b = parseInt(col.slice(5, 7), 16);
    const brightness = Math.sqrt(r * r * 0.241 + g * g * 0.691 + b * b * 0.068);
    return brightness > 130 ? '#000000' : '#FFFFFF';
  };

  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    borderRadius: 6,
    cursor: 'default',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(12),
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    color: getReadableColor(color),
    backgroundColor: color,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.grey[500_16]}`,
  };
});

interface Props {
  /** Background color in hex, e.g. '#FF0000' */
  color?: string;
  /** Additional custom styles */
  sx?: SxProps<Theme>;
  /** Label content */
  children?: React.ReactNode;
}

/**
 * TagLabel shows a span with a background color and readable text color.
 */
export default function TagLabel({ color = '#FFFFFF', sx, children, ...other }: Props) {
  return (
    <RootStyle ownerState={{ color }} sx={sx} {...other}>
      {children}
    </RootStyle>
  );
}
